import { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { Trash2, ExternalLink } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useUpdateCalendarEvent, useDeleteCalendarEvent } from "@/hooks/useCalendar";
import type { CalendarEvent } from "@/hooks/useCalendar";
import { toast } from "sonner";
import { formatError } from "@/lib/utils";

type Props = {
  event: CalendarEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EventEditDialog({ event, open, onOpenChange }: Props) {
  const updateEvent = useUpdateCalendarEvent();
  const deleteEvent = useDeleteCalendarEvent();

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [attendees, setAttendees] = useState("");

  // Sync form when event changes
  useEffect(() => {
    if (event) {
      setTitle(event.title || "");
      setDescription(event.description || "");
      setAttendees(event.attendees?.join(", ") || "");

      if (event.all_day) {
        setDate(event.start.slice(0, 10));
        setStartTime("09:00");
        setEndTime("10:00");
      } else {
        const start = parseISO(event.start);
        const end = parseISO(event.end);
        setDate(format(start, "yyyy-MM-dd"));
        setStartTime(format(start, "HH:mm"));
        setEndTime(format(end, "HH:mm"));
      }
    }
  }, [event]);

  if (!event) return null;

  const handleSave = () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }

    const startIso = `${date}T${startTime}:00`;
    const endIso = `${date}T${endTime}:00`;

    const attendeeList = attendees
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    updateEvent.mutate(
      {
        event_id: event.id,
        title: title.trim(),
        start_time: startIso,
        end_time: endIso,
        description,
        attendees: attendeeList,
      },
      {
        onSuccess: () => {
          toast("Event updated");
          onOpenChange(false);
        },
        onError: (err) => toast.error(formatError(err)),
      }
    );
  };

  const handleDelete = () => {
    deleteEvent.mutate(event.id, {
      onSuccess: () => {
        toast("Event deleted");
        onOpenChange(false);
      },
      onError: (err) => toast.error(formatError(err)),
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Event</SheetTitle>
          <SheetDescription className="sr-only">
            Edit or delete a Google Calendar event
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title"
            />
          </div>

          <div className="space-y-2">
            <Label>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Start</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>End</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Event details..."
              rows={3}
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label>Attendees</Label>
            <Input
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="email@example.com, another@example.com"
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated email addresses
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              className="flex-1"
              disabled={updateEvent.isPending}
            >
              {updateEvent.isPending ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="destructive"
              size="icon"
              onClick={handleDelete}
              disabled={deleteEvent.isPending}
              title="Delete event"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Link to Google Calendar */}
          <a
            href={event.html_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            Open in Google Calendar
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}
